"use client";

import { SparklesCore } from "@/components/ui/sparkles";
import { cn } from "@/lib/utils";
import { IconDotsVertical } from "@tabler/icons-react";
import { AnimatePresence, motion } from "motion/react";
import { useTheme } from "next-themes";
import Image from "next/image";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface CompareProps {
	firstImage?: string;
	darkFirstImage?: string;
	secondImage?: string;
	darkSecondImage?: string;
	className?: string;
	firstImageClassName?: string;
	secondImageClassname?: string;
	initialSliderPercentage?: number;
	slideMode?: "hover" | "drag";
	showHandlebar?: boolean;
	autoplay?: boolean;
	autoplayDuration?: number;
}
export const Compare = ({
	firstImage = "",
	darkFirstImage = "",
	secondImage = "",
	darkSecondImage = "",
	className,
	firstImageClassName,
	secondImageClassname,
	initialSliderPercentage = 50,
	slideMode = "hover",
	showHandlebar = true,
	autoplay = false,
	autoplayDuration = 5000,
}: CompareProps) => {
	const { theme } = useTheme();
	const [sliderXPercent, setSliderXPercent] = useState(initialSliderPercentage);
	const [isDragging, setIsDragging] = useState(false);
	const [mounted, setMounted] = useState(false);
	const hasMounted = useRef(false);

	useEffect(() => {
		if (!hasMounted.current) {
			hasMounted.current = true;
			setMounted(true);
		}
	}, []);

	const isDark = mounted && theme === "dark";
	const currentFirstImage =
		isDark && darkFirstImage ? darkFirstImage : firstImage;
	const currentSecondImage =
		isDark && darkSecondImage ? darkSecondImage : secondImage;

	const sliderRef = useRef<HTMLDivElement>(null);

	const [isMouseOver, setIsMouseOver] = useState(false);

	const autoplayRef = useRef<NodeJS.Timeout | null>(null);

	const startAutoplay = useCallback(() => {
		if (!autoplay) return;

		const startTime = Date.now();
		const animate = () => {
			const elapsedTime = Date.now() - startTime;
			const progress =
				(elapsedTime % (autoplayDuration * 2)) / autoplayDuration;
			const percentage = progress <= 1 ? progress * 100 : (2 - progress) * 100;

			setSliderXPercent(percentage);
			autoplayRef.current = setTimeout(animate, 16); // ~60fps
		};

		animate();
	}, [autoplay, autoplayDuration]);

	const stopAutoplay = useCallback(() => {
		if (autoplayRef.current) {
			clearTimeout(autoplayRef.current);
			autoplayRef.current = null;
		}
	}, []);

	useEffect(() => {
		startAutoplay();
		return () => stopAutoplay();
	}, [startAutoplay, stopAutoplay]);

	function mouseEnterHandler() {
		setIsMouseOver(true);
		stopAutoplay();
	}

	function mouseLeaveHandler() {
		setIsMouseOver(false);
		if (slideMode === "hover") {
			setSliderXPercent(initialSliderPercentage);
		}
		if (slideMode === "drag") {
			setIsDragging(false);
		}
		startAutoplay();
	}

	const handleStart = useCallback(
		(clientX: number) => {
			if (slideMode === "drag") {
				setIsDragging(true);
			}
		},
		[slideMode],
	);

	const handleEnd = useCallback(() => {
		if (slideMode === "drag") {
			setIsDragging(false);
		}
	}, [slideMode]);

	const handleMove = useCallback(
		(clientX: number) => {
			if (!sliderRef.current) return;
			if (slideMode === "hover" || (slideMode === "drag" && isDragging)) {
				const rect = sliderRef.current.getBoundingClientRect();
				const x = clientX - rect.left;
				const percent = (x / rect.width) * 100;
				requestAnimationFrame(() => {
					setSliderXPercent(Math.max(0, Math.min(100, percent)));
				});
			}
		},
		[slideMode, isDragging],
	);

	const handleMouseDown = useCallback(
		(e: React.MouseEvent) => handleStart(e.clientX),
		[handleStart],
	);
	const handleMouseUp = useCallback(() => handleEnd(), [handleEnd]);
	const handleMouseMove = useCallback(
		(e: React.MouseEvent) => handleMove(e.clientX),
		[handleMove],
	);

	const handleTouchStart = useCallback(
		(e: React.TouchEvent) => {
			if (!autoplay) {
				handleStart(e.touches[0].clientX);
			}
		},
		[handleStart, autoplay],
	);

	const handleTouchEnd = useCallback(() => {
		if (!autoplay) {
			handleEnd();
		}
	}, [handleEnd, autoplay]);

	const handleTouchMove = useCallback(
		(e: React.TouchEvent) => {
			if (!autoplay) {
				handleMove(e.touches[0].clientX);
			}
		},
		[handleMove, autoplay],
	);

	return (
		<div
			ref={sliderRef}
			className={cn("w-[400px] h-[400px] overflow-hidden", className)}
			style={{
				position: "relative",
				cursor: slideMode === "drag" ? "grab" : "col-resize",
			}}
			onMouseMove={handleMouseMove}
			onMouseLeave={mouseLeaveHandler}
			onMouseEnter={mouseEnterHandler}
			onMouseDown={handleMouseDown}
			onMouseUp={handleMouseUp}
			onTouchStart={handleTouchStart}
			onTouchEnd={handleTouchEnd}
			onTouchMove={handleTouchMove}
		>
			<AnimatePresence initial={false}>
				<motion.div
					className="h-full w-px absolute top-0 m-auto z-30 bg-gradient-to-b from-transparent from-[5%] to-[95%] via-indigo-500 to-transparent"
					style={{
						left: `${sliderXPercent}%`,
						top: "0",
						zIndex: 40,
					}}
					transition={{ duration: 0 }}
				>
					<div className="w-36 h-full [mask-image:radial-gradient(100px_at_left,white,transparent)] absolute top-1/2 -translate-y-1/2 left-0 bg-gradient-to-r from-indigo-400 via-transparent to-transparent z-20 opacity-50" />
					<div className="w-10 h-1/2 [mask-image:radial-gradient(50px_at_left,white,transparent)] absolute top-1/2 -translate-y-1/2 left-0 bg-gradient-to-r from-cyan-400 via-transparent to-transparent z-10 opacity-100" />
					<div className="w-10 h-3/4 top-1/2 -translate-y-1/2 absolute -right-10 [mask-image:radial-gradient(100px_at_left,white,transparent)]">
						<MemoizedSparklesCore
							background="transparent"
							minSize={0.4}
							maxSize={1}
							particleDensity={1200}
							className="w-full h-full"
							particleColor="#FFFFFF"
						/>
					</div>
					{showHandlebar && (
						<div className="h-5 w-5 rounded-md top-1/2 -translate-y-1/2 bg-white z-30 -right-2.5 absolute   flex items-center justify-center shadow-[0px_-1px_0px_0px_#FFFFFF40]">
							<IconDotsVertical className="h-4 w-4 text-black" />
						</div>
					)}
				</motion.div>
			</AnimatePresence>
			<div className="overflow-hidden w-full h-full relative z-20 pointer-events-none">
				<AnimatePresence initial={false}>
					{currentFirstImage ? (
						<motion.div
							className={cn(
								"absolute inset-0 z-20 rounded-2xl shrink-0 w-full h-full select-none overflow-hidden",
								firstImageClassName,
							)}
							style={{
								clipPath: `inset(0 ${100 - sliderXPercent}% 0 0)`,
							}}
							transition={{ duration: 0 }}
						>
							<Image
								alt="first image"
								src={currentFirstImage}
								className={cn(
									"absolute inset-0  z-20 rounded-2xl shrink-0 w-full h-full select-none",
									firstImageClassName,
								)}
								draggable={false}
								fill
								sizes="100%"
							/>
						</motion.div>
					) : null}
				</AnimatePresence>
			</div>

			<AnimatePresence initial={false}>
				{currentSecondImage ? (
					<motion.div
						className={cn(
							"absolute top-0 left-0 z-[19]  rounded-2xl w-full h-full select-none overflow-hidden",
							secondImageClassname,
						)}
					>
						<Image
							alt="second"
							src={currentSecondImage}
							className={cn(
								"absolute inset-0 z-[19] rounded-2xl w-full h-full select-none",
								secondImageClassname,
							)}
							draggable={false}
							fill
							sizes="100%"
						/>
					</motion.div>
				) : null}
			</AnimatePresence>
		</div>
	);
};

const MemoizedSparklesCore = React.memo(SparklesCore);
